import { useEffect, useRef, useState } from 'react';
import { POST_TYPE_OPTIONS, TOPIC_OPTIONS } from '../constants/hixrs';
import { formatRelativeTime, getPostSplit, getTopicLabel, getUserSettings, isOpenThought, userOwnsPost } from '../utils/hixrs';
import { getTotalComments } from '../utils/threads';
import { isLocalImageDataUrl, readCompressedImage } from '../services/imageUpload';
import { EmptyState, SplitBar } from '../components/common';

export function ProfileScreen({ user, profile, votes, posts, users, onSignOut, requireLogin, onSaveProfile, onOpenSettings, onPostDelete, onPostEdit, onOpenPost, onCreatePost }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [socialList, setSocialList] = useState(null);
  const profileImageRef = useRef();
  const bannerImageRef = useRef();
  const [draft, setDraft] = useState({
    displayName: '',
    username: '',
    bio: '',
    photoURL: '',
    bannerURL: ''
  });

  useEffect(() => {
    if (!user) return;
    const fallbackUsername = user.email?.split('@')[0]?.replace(/[^a-z0-9_]/gi, '').toLowerCase() || '';
    setDraft({
      displayName: profile?.displayName || user.displayName || '',
      username: profile?.username || fallbackUsername,
      bio: profile?.bio || '',
      photoURL: profile?.photoURL || user.photoURL || '',
      bannerURL: profile?.bannerURL || ''
    });
  }, [user, profile]);

  const handleProfileImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setProfileError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setProfileError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file, 640, 0.82);
      if (compressed.length > 900000) {
        setProfileError('This photo is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setDraft(prev => ({ ...prev, photoURL: compressed }));
      setProfileError('');
    } catch (err) {
      console.error(err);
      setProfileError('Could not read that photo. Try another file.');
      e.target.value = '';
    }
  };

  const removeProfileImage = () => {
    setDraft(prev => ({ ...prev, photoURL: '' }));
    if (profileImageRef.current) profileImageRef.current.value = '';
  };

  const handleBannerImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setProfileError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setProfileError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file, 1400, 0.82);
      if (compressed.length > 1400000) {
        setProfileError('This banner is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setDraft(prev => ({ ...prev, bannerURL: compressed }));
      setProfileError('');
    } catch (err) {
      console.error(err);
      setProfileError('Could not read that banner. Try another file.');
      e.target.value = '';
    }
  };

  const removeBannerImage = () => {
    setDraft(prev => ({ ...prev, bannerURL: '' }));
    if (bannerImageRef.current) bannerImageRef.current.value = '';
  };

  if (!user) {
    return (
      <div className="screen-wrap">
        <div className="guest-profile">
          <div className="gp-icon">ID</div>
          <div className="gp-title">Your opinion identity starts here</div>
          <div className="gp-sub">Sign in to build your Opinion Passport, track your lock-ins, and see where your positions stand.</div>
          <button className="google-btn" style={{ maxWidth: 320 }}
            onClick={() => requireLogin(() => {})}>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  const totalVotes = Object.keys(votes).length;
  const historyItems = Object.entries(votes)
    .map(([postId, side]) => {
      const post = posts.find(p => p.id === postId);
      if (!post) return null;
      const label = side === 'a' ? post.sideA : post.sideB;
      const pct = side === 'a' ? post.pctA : post.pctB;
      const isMaj = pct >= 50;
      return { postId, side, post, label, pct, isMaj };
    })
    .filter(Boolean);

  const minorityVotes = historyItems.filter(item => !item.isMaj).length;
  const minorityPct = totalVotes > 0 ? Math.round((minorityVotes / totalVotes) * 100) : 0;
  const ownPosts = posts.filter(p => userOwnsPost(p, user, profile));
  const userPosts = ownPosts.length;
  const followersList = users.filter(item => Array.isArray(item.following) && item.following.includes(user.uid));
  const followingIds = Array.isArray(profile?.following) ? profile.following : [];
  const followingList = followingIds.map(uid => users.find(item => item.uid === uid || item.id === uid)).filter(Boolean);
  const followersCount = followersList.length;
  const followingCount = followingIds.length;
  const ownCommentCount = ownPosts.reduce((sum, post) => sum + getTotalComments(post.comments || []), 0);
  const ownHeatCount = ownPosts.reduce((sum, post) => sum + Number(post.heatCount || 0), 0);
  const activityTotal = totalVotes + userPosts * 2 + ownCommentCount * 2 + ownHeatCount + followersCount * 3 + followingCount;
  const activityPct = Math.min(activityTotal * 7, 100);
  const identityType = totalVotes === 0
    ? userPosts > 0 ? 'Creator Signal' : 'Unranked'
    : minorityPct >= 45
      ? 'Contrarian'
      : minorityPct >= 25
        ? 'Independent Thinker'
        : 'Majority Reader';

  const topicCounts = historyItems.reduce((acc, item) => {
    const topic = getTopicLabel(item.post.topic);
    acc[topic] = (acc[topic] || 0) + 1;
    return acc;
  }, {});
  const topTopic = Object.entries(topicCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Unformed';
  const majorityPct = totalVotes > 0 ? 100 - minorityPct : 0;
  const displayName = profile?.displayName || user.displayName || 'User';
  const username = profile?.username || user.email?.split('@')[0] || 'hixr';
  const handle = username.startsWith('@') ? username : `@${username}`;
  const bio = profile?.bio || 'Building an opinion identity one lock-in at a time.';
  const photoURL = profile?.photoURL || user.photoURL || '';
  const bannerURL = profile?.bannerURL || '';
  const initial = displayName?.[0]?.toUpperCase() || 'U';
  const profileHeroStyle = bannerURL ? { '--profile-banner': `url("${bannerURL}")` } : {};
  const badgeItems = [
    { label: identityType, className: 'expert' },
    topTopic !== 'Unformed' && { label: `${topTopic} Voice`, className: 'minority-t' },
    userPosts >= 3 && { label: 'Hix Maker', className: 'spicy' },
    totalVotes >= 10 && { label: 'Consistent Locker', className: 'expert' },
    minorityPct >= 45 && { label: 'Contrarian Signal', className: 'contra' },
    minorityPct <= 20 && totalVotes >= 5 && { label: 'Room Reader', className: 'neutral' },
    totalVotes === 0 && { label: 'Lock in to build identity', className: 'neutral' }
  ].filter(Boolean);
  const visibleSocialList = socialList === 'followers' ? followersList : followingList;
  const socialTitle = socialList === 'followers' ? 'Followers' : 'Following';

  const saveProfile = async () => {
    const cleanName = draft.displayName.trim().slice(0, 40) || 'User';
    const cleanUsername = draft.username.trim().toLowerCase().replace(/^@/, '').replace(/[^a-z0-9_]/g, '').slice(0, 24);
    const cleanBio = draft.bio.trim().slice(0, 140);
    const cleanPhoto = draft.photoURL.trim();
    const cleanBanner = draft.bannerURL.trim();

    if (cleanUsername.length < 3) {
      setProfileError('Username must be at least 3 characters.');
      return;
    }

    setSaving(true);
    setProfileError('');
    try {
      await onSaveProfile({
        displayName: cleanName,
        username: cleanUsername,
        bio: cleanBio,
        photoURL: cleanPhoto,
        bannerURL: cleanBanner
      });
      setEditing(false);
    } catch (e) {
      console.error(e);
      setProfileError('Could not save profile. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen-wrap">
      <div className="profile-wrap">
        <div className={`profile-header passport-hero ${bannerURL ? 'has-profile-banner' : ''}`} style={profileHeroStyle}>
          {bannerURL && <img className="profile-banner-img" src={bannerURL} alt="" aria-hidden="true" />}
          <div className="p-avatar">
            {photoURL
              ? <img src={photoURL} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              : initial}
          </div>
          <div className="profile-main">
            <div className="profile-kicker">Opinion Passport</div>
            <div className="p-name">{displayName}</div>
            <div className="p-handle">{handle}</div>
            <div className="profile-bio">{bio}</div>
          </div>
          <div className="profile-actions">
            <div className="identity-badge">{identityType}</div>
            <button className="profile-settings-btn" onClick={onOpenSettings}>Settings</button>
            <button className="profile-edit-btn" onClick={() => { setEditing(!editing); setProfileError(''); }}>
              {editing ? 'Cancel' : 'Edit profile'}
            </button>
            <button className="signout-btn" onClick={onSignOut}>Sign out</button>
          </div>
        </div>

        {editing && (
          <div className="profile-editor">
            <div className="editor-title">Profile details</div>
            <div className="profile-form-grid">
              <label className="profile-field">
                <span>Name</span>
                <input value={draft.displayName} maxLength={40} onChange={e => setDraft(prev => ({ ...prev, displayName: e.target.value }))} />
              </label>
              <label className="profile-field">
                <span>Username</span>
                <input value={draft.username} maxLength={24} onChange={e => setDraft(prev => ({ ...prev, username: e.target.value }))} />
              </label>
            </div>
            <label className="profile-field">
              <span>Bio</span>
              <textarea rows={3} maxLength={140} value={draft.bio} onChange={e => setDraft(prev => ({ ...prev, bio: e.target.value }))} />
            </label>
            <div className="profile-image-picker">
              <div className="profile-image-preview">
                {draft.photoURL ? <img src={draft.photoURL} alt="Avatar preview" /> : initial}
              </div>
              <div className="profile-image-controls">
                <div className="profile-image-buttons">
                  <button type="button" className="image-upload-btn small" onClick={() => profileImageRef.current.click()}>Upload photo</button>
                  {draft.photoURL && <button type="button" className="image-remove-btn small" onClick={removeProfileImage}>Remove</button>}
                </div>
                <span className="profile-image-note">Device uploads are stored in Cloudinary after saving.</span>
                <input ref={profileImageRef} type="file" accept="image/*" onChange={handleProfileImage} style={{ display: 'none' }} />
              </div>
            </div>
            <label className="profile-field">
              <span>Avatar image URL</span>
              <input value={isLocalImageDataUrl(draft.photoURL) ? '' : draft.photoURL} onChange={e => setDraft(prev => ({ ...prev, photoURL: e.target.value }))} placeholder={isLocalImageDataUrl(draft.photoURL) ? 'Image selected from device' : 'https://...'} />
            </label>
            <div className="profile-banner-picker">
              <div className="profile-banner-preview">
                {draft.bannerURL ? <img src={draft.bannerURL} alt="Banner preview" /> : <span>Banner preview</span>}
              </div>
              <div className="profile-image-controls">
                <div className="profile-image-buttons">
                  <button type="button" className="image-upload-btn small" onClick={() => bannerImageRef.current.click()}>Upload banner</button>
                  {draft.bannerURL && <button type="button" className="image-remove-btn small" onClick={removeBannerImage}>Remove</button>}
                </div>
                <span className="profile-image-note">Use a wide image. It becomes the top profile banner.</span>
                <input ref={bannerImageRef} type="file" accept="image/*" onChange={handleBannerImage} style={{ display: 'none' }} />
              </div>
            </div>
            <label className="profile-field">
              <span>Banner image URL</span>
              <input value={isLocalImageDataUrl(draft.bannerURL) ? '' : draft.bannerURL} onChange={e => setDraft(prev => ({ ...prev, bannerURL: e.target.value }))} placeholder={isLocalImageDataUrl(draft.bannerURL) ? 'Image selected from device' : 'https://...'} />
            </label>
            {profileError && <div className="profile-error">{profileError}</div>}
            <div className="profile-editor-actions">
              <button className="profile-secondary-btn" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="profile-save-btn" onClick={saveProfile} disabled={saving}>{saving ? 'Saving...' : 'Save profile'}</button>
            </div>
          </div>
        )}

        <div className="stats-grid passport-stats-grid">
          <div className="stat-card"><div className="stat-num">{totalVotes}</div><div className="stat-lbl">Lock-ins made</div></div>
          <div className="stat-card"><div className="stat-num">{userPosts}</div><div className="stat-lbl">Hix posted</div></div>
          <button type="button" className="stat-card stat-card-button" onClick={() => setSocialList('followers')}><div className="stat-num">{followersCount}</div><div className="stat-lbl">Followers</div></button>
          <button type="button" className="stat-card stat-card-button" onClick={() => setSocialList('following')}><div className="stat-num">{followingCount}</div><div className="stat-lbl">Following</div></button>
          <div className="stat-card"><div className="stat-num">{minorityPct}%</div><div className="stat-lbl">Minority rate</div></div>
          <div className="stat-card"><div className="stat-num">{topTopic}</div><div className="stat-lbl">Top topic</div></div>
        </div>

        {socialList && (
          <div className="profile-social-panel">
            <div className="profile-section-head">
              <div>
                <div className="history-title">{socialTitle}</div>
                <div className="profile-section-sub">{visibleSocialList.length} {visibleSocialList.length === 1 ? 'person' : 'people'}</div>
              </div>
              <button type="button" className="profile-secondary-btn" onClick={() => setSocialList(null)}>Close</button>
            </div>
            {visibleSocialList.length === 0 ? (
              <EmptyState compact eyebrow={socialTitle} title={`No ${socialTitle.toLowerCase()} yet`} body="This list will fill as people follow each other." />
            ) : (
              <div className="profile-social-list">
                {visibleSocialList.map(item => {
                  const name = item.displayName || item.email || 'Hixrs user';
                  const handleText = item.username ? `@${item.username}` : item.email || '@hixr';
                  const avatar = item.photoURL || '';
                  return (
                    <div key={item.uid || item.id || handleText} className="profile-social-item">
                      <div className="profile-social-avatar">{avatar ? <img src={avatar} alt={name} /> : name[0]?.toUpperCase() || 'H'}</div>
                      <div><strong>{name}</strong><span>{handleText}</span></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="opinion-passport">
          <div className="op-head-row">
            <div>
              <div className="op-title">Identity Signals</div>
              <div className="op-subtitle">Built from all your lock-ins, posted Hixs, comments, heat, follows, and topic patterns.</div>
            </div>
          </div>
          <div className="op-tags">
            {badgeItems.map(badge => (
              <span key={badge.label} className={`op-tag ${badge.className}`}>{badge.label}</span>
            ))}
          </div>
          <div className="identity-card-grid">
            <div className="identity-card"><span>Type</span><strong>{identityType}</strong></div>
            <div className="identity-card"><span>Majority read</span><strong>{majorityPct}%</strong></div>
            <div className="identity-card"><span>Creator signal</span><strong>{userPosts > 0 ? `${userPosts} posts` : 'Not yet'}</strong></div>
            <div className="identity-card"><span>Discussion signal</span><strong>{ownCommentCount} comments</strong></div>
            <div className="identity-card"><span>Heat received</span><strong>{ownHeatCount}</strong></div>
            <div className="identity-card"><span>Total activity</span><strong>{activityTotal}</strong></div>
          </div>
          <div className="op-bar-row">
            <span className="op-bar-label">Contrarian</span>
            <div className="op-bar-track">
              <div className="op-bar-fill" style={{ width: minorityPct + '%', background: '#111827' }}></div>
            </div>
            <span className="op-bar-val" style={{ color: '#111827' }}>{minorityPct}%</span>
          </div>
          <div className="op-bar-row">
            <span className="op-bar-label">Activity</span>
            <div className="op-bar-track">
              <div className="op-bar-fill" style={{ width: activityPct + '%', background: '#2563EB' }}></div>
            </div>
            <span className="op-bar-val" style={{ color: '#2563EB' }}>{activityPct}%</span>
          </div>
        </div>

        <div className="profile-posts-panel">
          <div className="profile-section-head">
            <div>
              <div className="history-title">My Posts</div>
              <div className="profile-section-sub">Your Hixs and Open Thoughts live here so cleanup stays easy.</div>
            </div>
            <span>{ownPosts.length}</span>
          </div>
          {ownPosts.length === 0 ? (
            <EmptyState
              compact
              eyebrow="My Posts"
              title="No posts yet"
              body="Create your first Hix or Open Thought so it appears here for editing, opening, or deleting later."
              actionLabel="Create"
              onAction={onCreatePost} />
          ) : (
            <div className="my-hix-list">
              {ownPosts.slice(0, 10).map(post => {
                const split = getPostSplit(post);
                const thought = isOpenThought(post);
                return (
                  <div key={post.id} className={`my-hix-item ${thought ? 'my-thought-item' : ''}`}>
                    {post.image && <img src={post.image} alt="Hix" className="my-hix-thumb" />}
                    <div className="my-hix-copy">
                      <strong>{post.question}</strong>
                      <span>{getTopicLabel(post.topic)} - {thought ? `${getTotalComments(post.comments || [])} comments` : `${post.totalVotes || 0} lock-ins`} - {formatRelativeTime(post.createdAt)}</span>
                      {thought ? <em className="my-thought-badge">Open Thought</em> : <SplitBar pctA={split.pctA} className="my-hix-split" />}
                    </div>
                    <div className="my-hix-actions">
                      <button type="button" onClick={() => onOpenPost(post)}>Open</button>
                      <button type="button" onClick={() => onPostEdit(post)}>Edit</button>
                      <button type="button" className="danger" onClick={() => onPostDelete(post)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="history-panel">
          <div className="history-title">Lock-in History</div>
          {historyItems.length === 0
            ? <EmptyState
                compact
                eyebrow="History"
                title="No lock-ins yet"
                body="Lock in on Hixs to start building your opinion history." />
            : historyItems.map(({ postId, side, post, label, pct, isMaj }) => (
                <div key={postId} className="history-item">
                  <div className="hi-question">{post.question}</div>
                  <div className="hi-bottom">
                    <span className={`hi-side ${side === 'a' ? 'red-tag' : 'blue-tag'}`}>{label}</span>
                    <span className={`hi-result ${isMaj ? 'maj' : 'min'}`}>
                      {isMaj ? `Majority - ${pct}%` : `Minority - ${pct}%`}
                    </span>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
export function SettingsToggle({ checked, onChange }) {
  return (
    <button type="button" className={`settings-toggle ${checked ? 'on' : ''}`} aria-pressed={checked} onClick={() => onChange(!checked)}>
      <span></span>
    </button>
  );
}

export function SettingsScreen({ user, profile, onSaveProfile, onSaveSettings, onSignOut, onBack, requireLogin }) {
  const [activeTab, setActiveTab] = useState('account');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const settingsProfileImageRef = useRef();
  const settingsBannerImageRef = useRef();
  const [profileDraft, setProfileDraft] = useState({
    displayName: '',
    username: '',
    bio: '',
    photoURL: '',
    bannerURL: ''
  });
  const [settingsDraft, setSettingsDraft] = useState(getUserSettings(profile));

  useEffect(() => {
    if (!user) return;
    const fallbackUsername = user.email?.split('@')[0]?.replace(/[^a-z0-9_]/gi, '').toLowerCase() || '';
    setProfileDraft({
      displayName: profile?.displayName || user.displayName || '',
      username: profile?.username || fallbackUsername,
      bio: profile?.bio || '',
      photoURL: profile?.photoURL || user.photoURL || '',
      bannerURL: profile?.bannerURL || ''
    });
    setSettingsDraft(getUserSettings(profile));
  }, [user, profile]);

  const handleSettingsProfileImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSettingsError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setSettingsError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file, 640, 0.82);
      if (compressed.length > 900000) {
        setSettingsError('This photo is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setProfileDraft(prev => ({ ...prev, photoURL: compressed }));
      setSettingsError('');
      setSettingsMessage('');
    } catch (err) {
      console.error(err);
      setSettingsError('Could not read that photo. Try another file.');
      e.target.value = '';
    }
  };

  const removeSettingsProfileImage = () => {
    setProfileDraft(prev => ({ ...prev, photoURL: '' }));
    setSettingsMessage('');
    setSettingsError('');
    if (settingsProfileImageRef.current) settingsProfileImageRef.current.value = '';
  };

  const handleSettingsBannerImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSettingsError('Choose an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setSettingsError('Choose an image under 8 MB.');
      e.target.value = '';
      return;
    }

    try {
      const compressed = await readCompressedImage(file, 1400, 0.82);
      if (compressed.length > 1400000) {
        setSettingsError('This banner is still too large after compression. Try a smaller one.');
        e.target.value = '';
        return;
      }
      setProfileDraft(prev => ({ ...prev, bannerURL: compressed }));
      setSettingsError('');
      setSettingsMessage('');
    } catch (err) {
      console.error(err);
      setSettingsError('Could not read that banner. Try another file.');
      e.target.value = '';
    }
  };

  const removeSettingsBannerImage = () => {
    setProfileDraft(prev => ({ ...prev, bannerURL: '' }));
    setSettingsMessage('');
    setSettingsError('');
    if (settingsBannerImageRef.current) settingsBannerImageRef.current.value = '';
  };

  if (!user) {
    return (
      <div className="screen-wrap">
        <div className="settings-wrap guest-settings-wrap">
          <div className="guest-profile">
            <div className="gp-icon">ID</div>
            <div className="gp-title">Settings need an account</div>
            <div className="gp-sub">Sign in to manage profile, privacy, preferences, and notifications.</div>
            <button className="google-btn" style={{ maxWidth: 320 }} onClick={() => requireLogin(() => {})}>Sign in with Google</button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'account', label: 'Account' },
    { id: 'profile', label: 'Profile' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'safety', label: 'Safety' }
  ];

  const updateSetting = (section, key, value) => {
    setSettingsMessage('');
    setSettingsError('');
    setSettingsDraft(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const togglePreferredTopic = (topicKey) => {
    const current = settingsDraft.preferences.preferredTopics || [];
    const next = current.includes(topicKey)
      ? current.filter(item => item !== topicKey)
      : [...current, topicKey].slice(0, 8);
    updateSetting('preferences', 'preferredTopics', next);
  };

  const saveProfileSettings = async () => {
    const cleanName = profileDraft.displayName.trim().slice(0, 40) || 'User';
    const cleanUsername = profileDraft.username.trim().toLowerCase().replace(/^@/, '').replace(/[^a-z0-9_]/g, '').slice(0, 24);
    const cleanBio = profileDraft.bio.trim().slice(0, 140);
    const cleanPhoto = profileDraft.photoURL.trim();
    const cleanBanner = profileDraft.bannerURL.trim();

    if (cleanUsername.length < 3) {
      setSettingsError('Username must be at least 3 characters.');
      return;
    }

    setSavingProfile(true);
    setSettingsError('');
    setSettingsMessage('');
    try {
      await onSaveProfile({ displayName: cleanName, username: cleanUsername, bio: cleanBio, photoURL: cleanPhoto, bannerURL: cleanBanner });
      setSettingsMessage('Profile settings saved.');
    } catch (e) {
      console.error(e);
      setSettingsError('Could not save profile settings.');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsError('');
    setSettingsMessage('');
    try {
      await onSaveSettings(settingsDraft);
      setSettingsMessage('Settings saved.');
    } catch (e) {
      console.error(e);
      setSettingsError('Could not save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const profilePhoto = profileDraft.photoURL || user.photoURL || '';
  const profileName = profileDraft.displayName || user.displayName || 'User';
  const profileInitial = profileName?.[0]?.toUpperCase() || 'U';

  return (
    <div className="screen-wrap">
      <div className="settings-wrap">
        <div className="settings-header">
          <div>
            <div className="create-kicker">Control Center</div>
            <div className="settings-title">Settings</div>
            <div className="settings-subtitle">Manage your Hixrs account, identity, privacy, and notifications.</div>
          </div>
          <button type="button" className="settings-back-btn" onClick={onBack}>Back to profile</button>
        </div>

        <div className="settings-shell">
          <div className="settings-tabs" role="tablist" aria-label="Settings sections">
            {tabs.map(tab => (
              <button key={tab.id} type="button" className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => { setActiveTab(tab.id); setSettingsMessage(''); setSettingsError(''); }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="settings-panel">
            {settingsMessage && <div className="settings-success">{settingsMessage}</div>}
            {settingsError && <div className="settings-error">{settingsError}</div>}

            {activeTab === 'account' && (
              <div className="settings-section">
                <div className="settings-section-title">General</div>
                <div className="settings-row">
                  <div><strong>Email address</strong><span>The email connected to your Google login.</span></div>
                  <em>{user.email || 'No email'}</em>
                </div>
                <div className="settings-row">
                  <div><strong>Google account</strong><span>Used to sign in to Hixrs.</span></div>
                  <em>Connected</em>
                </div>
                <div className="settings-row">
                  <div><strong>Account type</strong><span>Your current Hixrs access level.</span></div>
                  <em>Member</em>
                </div>
                <div className="settings-row">
                  <div><strong>Sign out</strong><span>Leave this device and return to public Hixs.</span></div>
                  <button type="button" className="settings-outline-btn" onClick={onSignOut}>Sign out</button>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="settings-section-title">Profile Identity</div>
                <div className="settings-profile-card">
                  <div className="settings-avatar-preview">
                    {profilePhoto ? <img src={profilePhoto} alt="Profile preview" /> : profileInitial}
                  </div>
                  <div><strong>{profileName}</strong><span>@{profileDraft.username || 'hixr'}</span></div>
                </div>
                <div className="profile-image-picker settings-image-picker">
                  <div className="profile-image-preview">
                    {profilePhoto ? <img src={profilePhoto} alt="Profile upload preview" /> : profileInitial}
                  </div>
                  <div className="profile-image-controls">
                    <div className="profile-image-buttons">
                      <button type="button" className="image-upload-btn small" onClick={() => settingsProfileImageRef.current.click()}>Upload photo</button>
                      {profileDraft.photoURL && <button type="button" className="image-remove-btn small" onClick={removeSettingsProfileImage}>Remove</button>}
                    </div>
                    <span className="profile-image-note">Device uploads are stored in Cloudinary after saving.</span>
                    <input ref={settingsProfileImageRef} type="file" accept="image/*" onChange={handleSettingsProfileImage} style={{ display: 'none' }} />
                  </div>
                </div>
                <div className="settings-form-grid">
                  <label className="profile-field">
                    <span>Name</span>
                    <input value={profileDraft.displayName} maxLength={40} onChange={e => setProfileDraft(prev => ({ ...prev, displayName: e.target.value }))} />
                  </label>
                  <label className="profile-field">
                    <span>Username</span>
                    <input value={profileDraft.username} maxLength={24} onChange={e => setProfileDraft(prev => ({ ...prev, username: e.target.value }))} />
                  </label>
                </div>
                <label className="profile-field">
                  <span>Bio</span>
                  <textarea rows={3} maxLength={140} value={profileDraft.bio} onChange={e => setProfileDraft(prev => ({ ...prev, bio: e.target.value }))} />
                </label>
                <label className="profile-field">
                  <span>Profile photo URL</span>
                  <input value={isLocalImageDataUrl(profileDraft.photoURL) ? '' : profileDraft.photoURL} onChange={e => setProfileDraft(prev => ({ ...prev, photoURL: e.target.value }))} placeholder={isLocalImageDataUrl(profileDraft.photoURL) ? 'Image selected from device' : 'https://...'} />
                </label>
                <div className="profile-banner-picker settings-banner-picker">
                  <div className="profile-banner-preview">
                    {profileDraft.bannerURL ? <img src={profileDraft.bannerURL} alt="Banner upload preview" /> : <span>Banner preview</span>}
                  </div>
                  <div className="profile-image-controls">
                    <div className="profile-image-buttons">
                      <button type="button" className="image-upload-btn small" onClick={() => settingsBannerImageRef.current.click()}>Upload banner</button>
                      {profileDraft.bannerURL && <button type="button" className="image-remove-btn small" onClick={removeSettingsBannerImage}>Remove</button>}
                    </div>
                    <span className="profile-image-note">A wide banner makes your profile page feel more personal.</span>
                    <input ref={settingsBannerImageRef} type="file" accept="image/*" onChange={handleSettingsBannerImage} style={{ display: 'none' }} />
                  </div>
                </div>
                <label className="profile-field">
                  <span>Profile banner URL</span>
                  <input value={isLocalImageDataUrl(profileDraft.bannerURL) ? '' : profileDraft.bannerURL} onChange={e => setProfileDraft(prev => ({ ...prev, bannerURL: e.target.value }))} placeholder={isLocalImageDataUrl(profileDraft.bannerURL) ? 'Image selected from device' : 'https://...'} />
                </label>
                <div className="settings-actions"><button type="button" className="profile-save-btn" onClick={saveProfileSettings} disabled={savingProfile}>{savingProfile ? 'Saving...' : 'Save profile'}</button></div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="settings-section">
                <div className="settings-section-title">Privacy</div>
                <div className="settings-row"><div><strong>Public profile</strong><span>Allow other people to open your profile.</span></div><SettingsToggle checked={settingsDraft.privacy.publicProfile} onChange={value => updateSetting('privacy', 'publicProfile', value)} /></div>
                <div className="settings-row"><div><strong>Show followers</strong><span>Display your follower count on profile.</span></div><SettingsToggle checked={settingsDraft.privacy.showFollowers} onChange={value => updateSetting('privacy', 'showFollowers', value)} /></div>
                <div className="settings-row"><div><strong>Show following</strong><span>Display how many people you follow.</span></div><SettingsToggle checked={settingsDraft.privacy.showFollowing} onChange={value => updateSetting('privacy', 'showFollowing', value)} /></div>
                <div className="settings-row"><div><strong>Show lock-in history</strong><span>Let your profile show recent Hix choices later.</span></div><SettingsToggle checked={settingsDraft.privacy.showLockHistory} onChange={value => updateSetting('privacy', 'showLockHistory', value)} /></div>
                <div className="settings-row"><div><strong>Show Opinion Passport</strong><span>Display your identity stats on profile.</span></div><SettingsToggle checked={settingsDraft.privacy.showOpinionPassport} onChange={value => updateSetting('privacy', 'showOpinionPassport', value)} /></div>
                <div className="settings-row"><div><strong>Profile discovery</strong><span>Allow your account to appear in future people search.</span></div><SettingsToggle checked={settingsDraft.privacy.allowDiscovery} onChange={value => updateSetting('privacy', 'allowDiscovery', value)} /></div>
                <div className="settings-actions"><button type="button" className="profile-save-btn" onClick={saveSettings} disabled={savingSettings}>{savingSettings ? 'Saving...' : 'Save privacy'}</button></div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="settings-section">
                <div className="settings-section-title">Preferences</div>
                <div className="settings-row settings-row-stack">
                  <div>
                    <strong>Theme</strong>
                    <span>Use your device theme automatically, or force light/dark mode.</span>
                  </div>
                  <div className="settings-segment theme-segment" aria-label="Theme preference">
                    <button className={(settingsDraft.preferences.theme || 'system') === 'system' ? 'active' : ''} onClick={() => updateSetting('preferences', 'theme', 'system')}>Auto</button>
                    <button className={settingsDraft.preferences.theme === 'light' ? 'active' : ''} onClick={() => updateSetting('preferences', 'theme', 'light')}>Light</button>
                    <button className={settingsDraft.preferences.theme === 'dark' ? 'active' : ''} onClick={() => updateSetting('preferences', 'theme', 'dark')}>Dark</button>
                  </div>
                </div>
                <div className="settings-row settings-row-stack"><div><strong>Hixs density</strong><span>Choose how much room posts use while browsing Hixs.</span></div><div className="settings-segment"><button className={settingsDraft.preferences.feedDensity === 'comfortable' ? 'active' : ''} onClick={() => updateSetting('preferences', 'feedDensity', 'comfortable')}>Comfortable</button><button className={settingsDraft.preferences.feedDensity === 'compact' ? 'active' : ''} onClick={() => updateSetting('preferences', 'feedDensity', 'compact')}>Compact</button></div></div>
                <div className="settings-row settings-row-stack"><div><strong>Default post type</strong><span>The post type selected first when creating a Hix.</span></div><div className="settings-segment">{POST_TYPE_OPTIONS.map(type => <button key={type.key} className={settingsDraft.preferences.defaultPostType === type.key ? 'active' : ''} onClick={() => updateSetting('preferences', 'defaultPostType', type.key)}>{type.label}</button>)}</div></div>
                <div className="settings-topic-block"><strong>Preferred topics</strong><span>Pick topics you want Hixrs to learn from later.</span><div className="settings-topic-grid">{TOPIC_OPTIONS.slice(0, 12).map(topic => <button key={topic.key} type="button" className={(settingsDraft.preferences.preferredTopics || []).includes(topic.key) ? 'active' : ''} onClick={() => togglePreferredTopic(topic.key)}>{topic.label}</button>)}</div></div>
                <div className="settings-actions"><button type="button" className="profile-save-btn" onClick={saveSettings} disabled={savingSettings}>{savingSettings ? 'Saving...' : 'Save preferences'}</button></div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="settings-section">
                <div className="settings-section-title">Notifications</div>
                <div className="settings-row"><div><strong>Comments on my Hixs</strong><span>Tell me when someone comments on a Hix I posted.</span></div><SettingsToggle checked={settingsDraft.notifications.comments} onChange={value => updateSetting('notifications', 'comments', value)} /></div>
                <div className="settings-row"><div><strong>Replies</strong><span>Tell me when someone replies to my comment.</span></div><SettingsToggle checked={settingsDraft.notifications.replies} onChange={value => updateSetting('notifications', 'replies', value)} /></div>
                <div className="settings-row"><div><strong>New followers</strong><span>Tell me when someone follows me.</span></div><SettingsToggle checked={settingsDraft.notifications.followers} onChange={value => updateSetting('notifications', 'followers', value)} /></div>
                <div className="settings-row"><div><strong>Heat on my Hixs</strong><span>Tell me when a Hix gets heat.</span></div><SettingsToggle checked={settingsDraft.notifications.heat} onChange={value => updateSetting('notifications', 'heat', value)} /></div>
                <div className="settings-row"><div><strong>Weekly recap</strong><span>Future weekly identity and activity summary.</span></div><SettingsToggle checked={settingsDraft.notifications.weeklyRecap} onChange={value => updateSetting('notifications', 'weeklyRecap', value)} /></div>
                <div className="settings-row"><div><strong>Product updates</strong><span>Occasional Hixrs feature announcements.</span></div><SettingsToggle checked={settingsDraft.notifications.productUpdates} onChange={value => updateSetting('notifications', 'productUpdates', value)} /></div>
                <div className="settings-actions"><button type="button" className="profile-save-btn" onClick={saveSettings} disabled={savingSettings}>{savingSettings ? 'Saving...' : 'Save notifications'}</button></div>
              </div>
            )}

            {activeTab === 'safety' && (
              <div className="settings-section">
                <div className="settings-section-title">Safety</div>
                <div className="guidelines-card">
                  <div className="guidelines-kicker">Community Guidelines</div>
                  <h3>Keep Hixrs sharp without making it hostile.</h3>
                  <p>Hixrs is built for strong opinions, quick judgment, and better discussion after people lock in. The line is simple: attack ideas, not people.</p>
                  <div className="guidelines-list">
                    <div><strong>No harassment</strong><span>No targeting, bullying, doxxing, or repeated personal attacks.</span></div>
                    <div><strong>No hate or slurs</strong><span>No content attacking people for identity, religion, race, gender, sexuality, caste, nationality, or disability.</span></div>
                    <div><strong>No threats</strong><span>No violent threats, self-harm encouragement, or calls for real-world harm.</span></div>
                    <div><strong>No spam</strong><span>No repeated links, scams, fake engagement, or low-effort flooding.</span></div>
                    <div><strong>No explicit content</strong><span>No nudity, sexual content involving minors, or graphic shock content.</span></div>
                    <div><strong>Use reports</strong><span>Report bad Hixs or comments. Reports help us review and improve moderation.</span></div>
                  </div>
                </div>
                <div className="settings-row"><div><strong>Blocked users</strong><span>Block and mute controls will live here.</span></div><em>Coming soon</em></div>
                <div className="settings-row"><div><strong>Report a problem</strong><span>Send product or safety issues to the team.</span></div><em>Coming soon</em></div>
                <div className="settings-row danger"><div><strong>Delete account</strong><span>This will require a dedicated safety flow later.</span></div><em>Coming soon</em></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
